import { useEffect, useState, useCallback, useContext } from 'react';
import { AudioAutoPlayerContext } from '@/shared/Players/AudioAutoPlayer/AudioAutoPlayerContext';
import { FullscreenContext } from '@/shared/FullscreenContext/FullscreenContext';
import useShortcut from '@/hooks/useShortCutHook';

export default function useInteractiveVideoPlayer({
  questions,
  activityActions,
  forceRestart,
  noSeek,
  onVideoTimeChange,
}) {
  const [state, setState] = useState({
    questions: [],
    cues: [],
    answeredQuestions: [],
    currentQuestion: null,
    started: false,
    showSummary: false,
    restart: false,
    noSeek,
    cheatSeek: false,
    isFullscreen: false,
  });
  const fullscreenContext = useContext(FullscreenContext);
  const audioAutoPlayerContext = useContext(AudioAutoPlayerContext);

  const actions = {
    setState: (newState) => {
      setState((previousState) => ({ ...previousState, ...newState }));
    },

    shared: {
      onStart: async () => {
        if (activityActions) {
          await activityActions.onActivityStart();
        }
      },
      onPlay: () => {
        console.log(
          '[useInteractiveVideoActivity onPlay] Activating the autoPlay audio player'
        );
        audioAutoPlayerContext.actions.activate();
      },
      onProgress: ({ playedSeconds }) => {
        if (state.started) {
          if (onVideoTimeChange) {
            onVideoTimeChange(playedSeconds);
          }
        } else if (playedSeconds > 0) {
          if (!state.started || state.restart) {
            actions.setState({
              started: true,
              restart: false,
            });
          }
        }
      },
      onQuestionCue: (question) => {
        actions.setState({
          currentQuestion: question,
        });
        if (activityActions?.onQuestion) {
          activityActions.onQuestion(question);
        }
      },
      onQuestionAnswer: (answerData) => {
        const answeredQuestions = [
          ...state.answeredQuestions,
          { ...answerData, title: answerData.title.text },
        ];
        actions.setState({
          answeredQuestions,
        });
        if (activityActions) {
          activityActions.onActivityProgress({
            progress: Math.ceil(
              (100 * answeredQuestions.length) / state.questions.length
            ),
            correct: Math.ceil(
              (100 * countCorrectAnswers(answeredQuestions)) /
                state.questions.length
            ),
            answers: answeredQuestions,
          });
        }
      },
      onContinue: () => {
        if (state.currentQuestion) {
          actions.setState({
            currentQuestion: null,
            showSummary: state.currentQuestion.id == state.questions.length - 1,
          });
        }
        if (state.showSummary) {
          actions.setState({
            showSummary: false,
          });
          if (activityActions?.goToNextTaskActivity) {
            activityActions.goToNextTaskActivity();
          }
        }
      },
      onRestart: async () => {
        actions.setState({
          restart: true,
          showSummary: false,
          answeredQuestions: [],
          started: false,
          currentQuestion: null,
        });
        if (activityActions) {
          activityActions.onActivityRestart();
        }
      },
      onFullScreen: () => {
        fullscreenContext.actions.setFullscreen(
          !fullscreenContext.state.isFullscreen
        );
      },
      onSeek: () => {
        actions.setState({
          currentQuestion: null,
        });
      },
    },
  };

  useShortcut({
    shortcut: 'Ctrl+Shift+Alt+T',
    handler: () => {
      actions.setState({ noSeek: !state.noSeek });
    },
  });

  useEffect(() => {
    return () => {
      audioAutoPlayerContext.actions.reset();
    };
  }, []);

  useEffect(() => {
    actions.setState({ questions });
  }, [questions]);

  useEffect(() => {
    if (forceRestart) {
      actions.shared.onRestart();
    }
  }, [forceRestart]);

  useEffect(() => {
    const newNoSeek = noSeek && (!state.cheatSeek || !!state.currentQuestion);
    actions.setState({
      noSeek: newNoSeek,
    });
    console.log('Cheat seek activated?', !newNoSeek);
  }, [state.cheatSeek]);

  const activateCheatSeek = useCallback(() => {
    console.log('Switching cheatSeek...');
    actions.setState({ cheatSeek: !state.cheatSeek });
  });

  useEffect(() => {
    actions.setState({ isFullscreen: fullscreenContext.state.isFullscreen });
  }, [fullscreenContext.state.isFullscreen]);

  return { state, actions };
}

export function countCorrectAnswers(answers) {
  return answers.reduce((acc, cur) => (cur.correct ? acc + 1 : acc), 0);
}
